// ── Business category validation helpers ──────────────────────────────
// Emojis / pictographs / dingbats / flags / variation selectors.
const EMOJI_REGEX = /[\u{1F000}-\u{1FAFF}\u{2600}-\u{27BF}\u{2190}-\u{21FF}\u{2300}-\u{23FF}\u{2B00}-\u{2BFF}\u{FE00}-\u{FE0F}\u{1F1E6}-\u{1F1FF}\u{200D}\u{20E3}\u{2122}\u{2139}]/u;

// Allowed: unicode letters (incl. accented like "Café"), digits, spaces and a
// small set of business punctuation ( & / ' ’ . , ( ) - ) used by real
// categories such as "Car & Bus Services" or "Men’s Salon".
const ALLOWED_CATEGORY_CHARS = /^[\p{L}\p{N}\s&/'’.,()\-]+$/u;

// Obvious keyboard-walk / mashing sequences used to catch gibberish.
const KEYBOARD_WALKS = [
  'qwert', 'werty', 'ertyu', 'asdf', 'sdfg', 'dfgh', 'fghj', 'zxcv', 'xcvb',
  'cvbn', 'qazwsx', 'wsxedc', 'poiuy', 'lkjh', 'mnbv', 'hjkl'
];

// Returns an error string for emojis, special characters, excessively long
// text, or random / meaningless input — otherwise null.
export const validateBusinessCategory = (rawValue) => {
  const value = String(rawValue ?? '').trim();

  if (value === '') return 'Please select a business type';
  if (EMOJI_REGEX.test(value)) return 'Emojis are not allowed in the business category';
  if (value.length < 2) return 'Business category must be at least 2 characters';
  if (value.length > 50) return 'Business category must be 50 characters or less';
  if (!ALLOWED_CATEGORY_CHARS.test(value)) {
    return 'Special characters are not allowed in the business category';
  }

  // Must contain real alphabetic content — not only digits / punctuation.
  const letters = value.replace(/[^\p{L}]/gu, '');
  if (letters.length < 2) return 'Please enter a valid business category';

  const lower = letters.toLowerCase();

  // Same character repeated 4+ times in a row → "aaaa", "zzzz".
  if (/(.)\1{3,}/u.test(lower)) return 'Please enter a valid business category';

  // No vowel at all → random consonant mashing like "xkcd" / "kjhg".
  if (!/[aeiouy]/.test(lower)) return 'Please enter a valid business category';

  // A long run of consonants inside any word → "asdfgh".
  const hasConsonantMash = value
    .split(/\s+/)
    .some((w) => /[bcdfghjklmnpqrstvwxz]{5,}/.test(w.replace(/[^\p{L}]/gu, '').toLowerCase()));
  if (hasConsonantMash) return 'Please enter a valid business category';

  // Obvious keyboard walks → "qwerty", "asdfgh", "zxcvb".
  if (KEYBOARD_WALKS.some((seq) => lower.includes(seq))) {
    return 'Please enter a valid business category';
  }

  return null;
};

export const validationRules = {
  businessType: {
    required: true,
    validate: validateBusinessCategory,
    message: 'Please select a business type'
  },
  displayBusinessName: {
    required: true,
    minLength: 2,
    message: 'Business name is required (min 2 characters)'
  },
  businessUrl: {
    pattern: /^https?:\/\/.+/,
    message: 'Please enter a valid URL (http:// or https://)'
  },
  email: {
    pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
    message: 'Please enter a valid email'
  },
  phone: {
    pattern: /^\+[1-9]\d{9,14}$/,
    message: 'Please enter a valid  phone number (e.g., +1234567890)'
  }
};

export const validateField = (fieldName, value) => {
  const rules = validationRules[fieldName];
  if (!rules) return null;

  if (rules.required && (!value || String(value).trim() === '')) {
    return rules.message;
  }

  // Field-specific custom validator (e.g. business category gibberish checks).
  if (rules.validate) {
    return rules.validate(value);
  }

  if (rules.minLength && value && value.length < rules.minLength) {
    return `Must be at least ${rules.minLength} characters`;
  }

  if (rules.pattern && value) {
    // Clean phone number for validation
    if (fieldName === 'phone') {
      const cleaned = String(value).replace(/[\s\-\(\)]/g, '');
      if (!rules.pattern.test(cleaned)) {
        return rules.message;
      }
    } else if (!rules.pattern.test(value)) {
      return rules.message;
    }
  }

  return null;
};